import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ImagePlus, LinkIcon, FileText, Trash2, Archive, RefreshCw, Check, ChevronRight, Inbox as InboxIcon, ShieldAlert, ArrowLeft } from "lucide-react";
import { intakeApi, entitiesApi, projectsApi, filesApi } from "@/lib/api";
import { showIntakeConfirmToast } from "@/lib/intakeToast";
import { useAuth } from "@/lib/auth";
import SendToCompassButton from "@/components/compass/SendToCompassButton";

/**
 * HQ Intake v1 — Capture Once, Route Intelligently.
 *
 * Two panes:
 *   • left column — history of past submissions (click to open)
 *   • main column — one of three states:
 *       1. blank capture form (source_type toggle + input)
 *       2. review screen (one card per suggestion, editable, include/exclude)
 *       3. routed confirmation (created records + link back to entities)
 *
 * The user is never taken away from this page mid-flow; every write happens
 * with an explicit Confirm click on the review screen.
 */

const SOURCE_TABS = [
  { key: "text", label: "Text", icon: FileText },
  { key: "url", label: "URL", icon: LinkIcon },
  { key: "image", label: "Image", icon: ImagePlus },
];

export default function IntakePage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [history, setHistory] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(params.get("id") || null);
  const [current, setCurrent] = useState(null);       // IntakeItem being reviewed
  const [loading, setLoading] = useState(false);

  // Capture form state
  const [sourceType, setSourceType] = useState("text");
  const [textVal, setTextVal] = useState("");
  const [urlVal, setUrlVal] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageFileId, setImageFileId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // `submittedAck` is populated for restricted contributors when their
  // submission has no visible routing matches — we render the neutral
  // "Submitted to HQ" card instead of a dead-end empty review pane.
  const [submittedAck, setSubmittedAck] = useState(null);

  // Recovery state — populated when a URL analyse returned
  // `status: "fetch_blocked"`. Keeps the original URL as provenance
  // while the user retries via screenshot or pasted content.
  const [blockedFetch, setBlockedFetch] = useState(null);
  const [recoveryMode, setRecoveryMode] = useState(null); // null | "screenshot" | "paste"

  // Review state
  const [confirmedFlags, setConfirmedFlags] = useState({}); // {suggestion_id: bool}
  const [suggEdits, setSuggEdits] = useState({});           // {suggestion_id: {field: value}}
  const [confirming, setConfirming] = useState(false);

  const refreshHistory = async () => {
    const r = await intakeApi.list();
    setHistory(r.data);
  };

  useEffect(() => {
    refreshHistory();
    Promise.all([entitiesApi.list(), projectsApi.list()]).then(([e, p]) => {
      setEntities(e.data); setProjects(p.data);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) { setCurrent(null); return; }
    setLoading(true);
    intakeApi.get(selectedId)
      .then(r => {
        setCurrent(r.data);
        // Default every suggestion to INCLUDED so the user only needs
        // to uncheck what they don't want.
        const flags = {};
        (r.data.suggestions || []).forEach(s => { flags[s.id] = true; });
        setConfirmedFlags(flags);
        setSuggEdits({});
      })
      .catch(() => setCurrent(null))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const startFresh = () => {
    setSelectedId(null); setCurrent(null); setParams({});
    setSourceType("text"); setTextVal(""); setUrlVal("");
    setImageFile(null); setImageFileId(null); setImagePreview(null);
    setBlockedFetch(null); setRecoveryMode(null);
    setSubmittedAck(null);
  };

  const uploadImage = async (f) => {
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      return toast.error("Only PNG / JPEG / WEBP images accepted");
    }
    if (f.size > 12 * 1024 * 1024) return toast.error("Image is over 12 MB");
    setUploading(true);
    try {
      const r = await filesApi.upload(f);
      setImageFile(f); setImageFileId(r.data.id);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(f);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const analyze = async () => {
    // Recovery-mode analysis routes through the existing text/image
    // pipeline, but keeps the original blocked URL as provenance via
    // the `recovery_source` field.
    const activeType = recoveryMode === "screenshot"
      ? "image"
      : recoveryMode === "paste"
      ? "text"
      : sourceType;
    if (activeType === "text" && !textVal.trim()) return toast.error("Paste some text first");
    if (activeType === "url" && !urlVal.trim()) return toast.error("Enter a URL");
    if (activeType === "image" && !imageFileId) return toast.error("Upload an image first");
    setAnalyzing(true);
    try {
      const payload = {
        source_type: activeType,
        text: activeType === "text" || activeType === "url" ? textVal : null,
        url: activeType === "url"
          ? urlVal
          : (blockedFetch ? blockedFetch.source_url : null),
        file_id: activeType === "image" ? imageFileId : null,
      };
      if (blockedFetch && recoveryMode === "screenshot") {
        payload.recovery_source = "url_screenshot";
      } else if (blockedFetch && recoveryMode === "paste") {
        payload.recovery_source = "url_paste";
      }
      const r = await intakeApi.analyze(payload);
      // Blocked fetch — no intake_item persisted server-side; surface
      // the recovery options inline instead of the dead-end state.
      if (r.data?.status === "fetch_blocked") {
        setBlockedFetch({
          source_url: r.data.source_url,
          reason: r.data.reason,
        });
        setRecoveryMode(null);
        setTextVal(""); setImageFile(null); setImageFileId(null); setImagePreview(null);
        return;
      }
      setBlockedFetch(null); setRecoveryMode(null);
      // Restricted contributor path: when the analyser produced no
      // visible suggestions (either genuinely no match, or every
      // strong hit was cross-entity), skip opening the routing
      // review pane. Show the neutral submitted-to-HQ confirmation
      // instead — this is the "Intake means bring information into
      // HQ" contract. The item is already persisted server-side and
      // admin can pick it up from history later.
      const isAdmin = (user?.platform_role || user?.role) === "admin";
      const noVisibleMatches = (r.data.suggestions || []).length === 0;
      if (!isAdmin && noVisibleMatches) {
        setSubmittedAck({
          cross_entity_ack: !!r.data.cross_entity_ack,
          status: r.data.status || "needs_review",
        });
        // Reset the capture form for a follow-up.
        setTextVal(""); setUrlVal("");
        setImageFile(null); setImageFileId(null); setImagePreview(null);
        await refreshHistory();
        return;
      }
      setSelectedId(r.data.id);
      setParams({ id: r.data.id });
      await refreshHistory();
      // Admin-side toast for the same signal-vs-no-match distinction
      // — admins still see the review pane so this is informative,
      // not the primary UX.
      if (r.data.cross_entity_ack) {
        toast.success("Submitted and reviewed", {
          description: "A cross-entity signal was captured for authorized reviewers.",
          duration: 6000,
        });
      } else if (noVisibleMatches) {
        toast.info("Saved as Needs Review", {
          description: "No confident routing match — archive or route manually from history.",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const suggValue = (sug, key) => {
    const eObj = suggEdits[sug.id] || {};
    return eObj[key] !== undefined ? eObj[key] : sug[key];
  };
  const updateSugg = (sug, patch) => {
    setSuggEdits(prev => ({ ...prev, [sug.id]: { ...(prev[sug.id] || {}), ...patch } }));
  };

  const confirmSave = async () => {
    if (!current) return;
    const chosen = (current.suggestions || [])
      .filter(s => confirmedFlags[s.id])
      .map(s => ({
        entity_id: suggValue(s, "entity_id"),
        project_id: suggValue(s, "project_id"),
        record_type: suggValue(s, "record_type"),
        title: suggValue(s, "title"),
        content: suggValue(s, "content"),
        contact: suggValue(s, "contact") || undefined,
        organization: suggValue(s, "organization") || undefined,
        why: suggValue(s, "why"),
        matched_signals: suggValue(s, "matched_signals"),
        confidence: suggValue(s, "confidence"),
      }));
    try {
      const r = await intakeApi.confirm(current.id, chosen);
      showIntakeConfirmToast(r.data.created);
      const fresh = await intakeApi.get(current.id);
      setCurrent(fresh.data);
      await refreshHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setConfirming(false); }
  };

  const archive = async () => {
    if (!current) return;
    await intakeApi.patch(current.id, { status: "archived" });
    toast.success("Archived");
    await refreshHistory();
    startFresh();
  };

  return (
    <div className="h-full overflow-hidden flex flex-col md:flex-row bg-[#0B0E14] text-white" data-testid="intake-page">
      {/* History sidebar */}
      <aside className="md:w-72 border-b md:border-b-0 md:border-r border-white/10 flex flex-col">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-eyebrow">Intake</div>
            <div className="font-display text-lg font-medium">Capture</div>
          </div>
          <Button size="sm" onClick={startFresh} className="rounded-full bg-white text-black hover:bg-white/90 h-8" data-testid="intake-new-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1.5"/> New
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="intake-history-list">
          {history.length === 0 && (
            <div className="text-xs text-white/40 p-4">No submissions yet. Drop something into Capture and HQ will route it.</div>
          )}
          {history.map(item => (
            <button key={item.id} onClick={() => { setSelectedId(item.id); setParams({ id: item.id }); }}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                selectedId === item.id
                  ? "border-white/20 bg-white/[0.06]"
                  : "border-transparent hover:bg-white/[0.03]"
              }`}
              data-testid={`intake-history-item-${item.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-1.5 mb-1">
                <SourceIcon type={item.source_type}/>
                <span>{item.source_type}</span>
                <span>·</span>
                <span>{item.status}</span>
              </div>
              <div className="text-sm text-white/85 line-clamp-2">
                {item.summary || item.original_text?.slice(0, 100) || item.url || "Intake"}
              </div>
              <div className="text-[10px] text-white/40 mt-1">
                {(item.suggestions || []).length} suggestion{(item.suggestions || []).length === 1 ? "" : "s"} · {(item.linked_records || []).length} routed
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main pane */}
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        {!selectedId ? (
          submittedAck ? (
            <ContributorSubmittedCard
              ack={submittedAck}
              onNew={() => setSubmittedAck(null)} />
          ) : blockedFetch ? (
            <FetchBlockedRecovery
              blocked={blockedFetch}
              recoveryMode={recoveryMode}
              setRecoveryMode={(m) => {
                setRecoveryMode(m);
                setTextVal(""); setImageFile(null);
                setImageFileId(null); setImagePreview(null);
              }}
              onCancel={() => { setBlockedFetch(null); setRecoveryMode(null); }}
              textVal={textVal} setTextVal={setTextVal}
              imageFile={imageFile} imagePreview={imagePreview}
              uploading={uploading} onUpload={uploadImage}
              analyzing={analyzing} onAnalyze={analyze}
            />
          ) : (
            <CaptureForm
              sourceType={sourceType} setSourceType={setSourceType}
              textVal={textVal} setTextVal={setTextVal}
              urlVal={urlVal} setUrlVal={setUrlVal}
              imageFile={imageFile} imagePreview={imagePreview}
              uploading={uploading} onUpload={uploadImage}
              analyzing={analyzing} onAnalyze={analyze}
            />
          )
        ) : loading || !current ? (
          <div className="text-white/50 text-sm">Loading…</div>
        ) : (
          <ReviewPane
            item={current}
            entities={entities}
            projects={projects}
            confirmedFlags={confirmedFlags}
            setConfirmedFlags={setConfirmedFlags}
            suggValue={suggValue}
            updateSugg={updateSugg}
            confirming={confirming}
            onConfirm={confirmSave}
            onArchive={archive}
            onLinkedRecordsChange={(fresh) => { if (fresh) setCurrent(fresh); refreshHistory(); }}
          />
        )}
      </main>
    </div>
  );
}

export function SourceIcon({ type }) {
  const I = type === "url" ? LinkIcon : type === "image" ? ImagePlus : FileText;
  return <I className="w-3 h-3"/>;
}

// ---------------------------------------------------------------------------
// Capture form — exported so Universal Quick Add's Intake tab renders the
// exact same UX. All state lives on the parent so both callers keep their
// own lifecycles (history nav vs modal close).
// ---------------------------------------------------------------------------
export function CaptureForm({ sourceType, setSourceType, textVal, setTextVal,
                       urlVal, setUrlVal, imageFile, imagePreview,
                       uploading, onUpload, analyzing, onAnalyze,
                       compact = false }) {
  return (
    <div className={compact ? "space-y-4" : "max-w-2xl space-y-6"} data-testid="intake-capture-form">
      {!compact && (
        <div>
          <div className="text-eyebrow">HQ Intake</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Drop it in.</h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            Paste text, share a URL, or upload a screenshot. HQ will summarize the source, extract explicit facts, and propose where it belongs across your entities — you decide what to save.
          </p>
        </div>
      )}

      <div className="flex items-center gap-1 p-1 rounded-full border border-white/10 bg-white/[0.03] w-fit" data-testid="intake-source-tabs">
        {SOURCE_TABS.map(t => {
          const I = t.icon;
          const active = sourceType === t.key;
          return (
            <button key={t.key} type="button" onClick={() => setSourceType(t.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${
                active ? "bg-white text-black" : "text-white/60 hover:text-white"
              }`}
              data-testid={`intake-tab-${t.key}`}>
              <I className="w-3.5 h-3.5"/> {t.label}
            </button>
          );
        })}
      </div>

      {sourceType === "text" && (
        <Textarea rows={10} value={textVal} onChange={(e) => setTextVal(e.target.value)}
          placeholder="Paste anything — an email, a research note, meeting minutes, a job description…"
          className="bg-white/5 border-white/10 text-sm"
          data-testid="intake-text-input"/>
      )}
      {sourceType === "url" && (
        <div className="space-y-3">
          <Input value={urlVal} onChange={(e) => setUrlVal(e.target.value)}
            placeholder="https://…" className="bg-white/5 border-white/10 h-11 text-sm"
            data-testid="intake-url-input"/>
          <Textarea rows={4} value={textVal} onChange={(e) => setTextVal(e.target.value)}
            placeholder="Optional: what caught your eye about this?" className="bg-white/5 border-white/10 text-sm"
            data-testid="intake-url-note"/>
          <div className="text-[11px] text-white/40">HQ will fetch the page and analyse its visible content.</div>
        </div>
      )}
      {sourceType === "image" && (
        <div className="space-y-3">
          <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/15 rounded-xl p-8 cursor-pointer hover:bg-white/[0.02]" data-testid="intake-image-drop">
            <ImagePlus className="w-6 h-6 text-white/40"/>
            <div className="text-sm text-white/70">
              {imageFile ? imageFile.name : "Click to upload — PNG, JPG, WEBP"}
            </div>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])} data-testid="intake-image-file"/>
          </label>
          {imagePreview && (
            <img src={imagePreview} alt="preview" className="max-h-64 rounded-lg border border-white/10"/>
          )}
          <Textarea rows={3} value={textVal} onChange={(e) => setTextVal(e.target.value)}
            placeholder="Optional: caption / what this is" className="bg-white/5 border-white/10 text-sm"/>
          {uploading && <div className="text-xs text-white/50">Uploading…</div>}
        </div>
      )}

      <Button onClick={onAnalyze} disabled={analyzing}
        className="rounded-full bg-white text-black hover:bg-white/90 h-11 px-6" data-testid="intake-analyze-btn">
        <Sparkles className="w-4 h-4 mr-2"/> {analyzing ? "Analysing…" : "Analyse & suggest destinations"}
      </Button>
      <div className="text-[11px] text-white/40">
        Nothing is saved until you confirm on the next screen. HQ separates explicit facts from inferred relevance and will never invent contact details or business facts.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review pane — exported for Universal Quick Add reuse. Handles evidence
// display, per-suggestion include/exclude + editable destination, contact
// dedupe-safe sub-form, and confirmation. Never writes on render — only
// on the explicit `onConfirm` button click.
// ---------------------------------------------------------------------------
export function ReviewPane({ item, entities, projects, confirmedFlags,
                      setConfirmedFlags, suggValue, updateSugg,
                      confirming, onConfirm, onArchive,
                      onLinkedRecordsChange,
                      compact = false, hideArchive = false }) {
  const included = (item.suggestions || []).filter(s => confirmedFlags[s.id]).length;
  const routed = item.status === "routed";
  return (
    <div className={compact ? "space-y-5" : "max-w-3xl space-y-5"} data-testid="intake-review-pane">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-eyebrow flex items-center gap-2">
            <SourceIcon type={item.source_type}/> {item.source_type} · {item.status}
          </div>
          <h1 className={`font-display font-medium tracking-tight mt-1 ${compact ? "text-lg" : "text-2xl"}`}>{item.summary || "Intake item"}</h1>
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-white/50 hover:text-white break-all" data-testid="intake-source-url">{item.url}</a>
          )}
          {item.recovery_source && (
            <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-300/90 bg-amber-500/[0.08] border border-amber-400/25 rounded-full px-2 py-0.5"
                 data-testid="intake-recovery-source">
              <ShieldAlert className="w-3 h-3"/>
              {item.recovery_source === "url_screenshot"
                ? "URL recovered via screenshot"
                : "URL recovered via pasted content"}
            </div>
          )}
        </div>
        {!hideArchive && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={onArchive} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="intake-archive-btn">
              <Archive className="w-3.5 h-3.5 mr-1.5"/> Archive
            </Button>
          </div>
        )}
      </div>

      {/* Evidence */}
      {(item.evidence || []).length > 0 && (
        <section className="p-4 rounded-xl border border-white/10 bg-[#151A23]" data-testid="intake-evidence">
          <div className="text-eyebrow mb-2">Explicit evidence</div>
          <ul className="space-y-1 text-sm text-white/80">
            {item.evidence.map((e, i) => (<li key={i} className="flex gap-2"><span className="text-white/30">·</span><span>{e}</span></li>))}
          </ul>
        </section>
      )}

      {/* Suggestions */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-eyebrow">Suggested destinations ({(item.suggestions || []).length})</div>
          <div className="text-xs text-white/40">{included} selected</div>
        </div>
        {(item.suggestions || []).length === 0 && (
          <div className="text-sm text-white/60 p-4 rounded-lg border border-white/10 bg-white/[0.02]">
            No routing matches found. Source is stored under Intake history — you can still archive it or write a manual note against an entity.
          </div>
        )}
        {(item.suggestions || []).map(sug => (
          <SuggestionCard
            key={sug.id}
            sug={sug}
            entities={entities}
            projects={projects}
            included={!!confirmedFlags[sug.id]}
            onToggle={() => setConfirmedFlags(f => ({ ...f, [sug.id]: !f[sug.id] }))}
            value={(k) => suggValue(sug, k)}
            onUpdate={(patch) => updateSugg(sug, patch)}
          />
        ))}
      </section>

      {/* Routed records */}
      {(item.linked_records || []).length > 0 && (
        <section className="p-4 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06]" data-testid="intake-linked-records">
          <div className="text-eyebrow text-emerald-300 mb-2">Routed records</div>
          <ul className="space-y-1 text-xs text-white/80">
            {item.linked_records.map((lr, i) => {
              const ent = entities.find((e) => e.id === lr.entity_id);
              const entName = lr.entity_name || (ent && ent.name) || "Entity";
              const proj = (lr.project_id
                ? projects.find((p) => p.id === lr.project_id)
                : null);
              const projName = lr.project_name || (proj && proj.name) || null;
              const isOrg = lr.type === "organization";
              const orgLabel = lr.organization_name || (isOrg ? "Organization" : lr.type);
              return (
                <li key={i} className="flex items-center gap-2 flex-wrap"
                    data-testid={`intake-linked-record-${i}`}>
                  <Check className="w-3 h-3 text-emerald-400 flex-shrink-0"/>
                  <span>
                    {isOrg
                      ? (lr.duplicate_of ? "Linked" : "Created organization")
                      : `Created ${lr.type}`}
                    {isOrg && <span className="text-white/60"> · {orgLabel}</span>}
                  </span>
                  <ChevronRight className="w-3 h-3 text-white/30"/>
                  <span className="text-white/90">{entName}</span>
                  {projName && (<>
                    <ChevronRight className="w-3 h-3 text-white/30"/>
                    <span className="text-white/70">{projName}</span>
                  </>)}
                  <Link to={`/entity/${lr.entity_id}`}
                        className="ml-auto text-white/60 hover:text-white text-[10px] underline underline-offset-2"
                        data-testid={`intake-linked-record-open-${i}`}>
                    Open entity
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Add relationship — routed intake with an existing Organization
          can be linked to additional Entity/Project combos without
          leaving Intake. Reuses server-side upsert dedup. */}
      {routed && item.linked_records?.some(l => l.type === "organization") && (
        <AddIntakeRelationship
          item={item}
          entities={entities}
          projects={projects}
          onAdded={onLinkedRecordsChange}/>
      )}

      {/* Send to Compass — routed intake with an Organization can be
          handed off to Compass evaluation without leaving Intake.
          Passes canonical org id so no duplicate record is created. */}
      {routed && item.linked_records?.some(l => l.type === "organization") && (
        <section className="pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Send to Compass:</span>
          {item.linked_records.filter(l => l.type === "organization").map(l => (
            <SendToCompassButton
              key={l.id} targetType="organization" targetId={l.id}
              label={`Evaluate ${l.organization_name || "Organization"}`}
              testid={`intake-send-to-compass-${l.id}`} />
          ))}
        </section>
      )}

      {!routed && (
        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={onConfirm} disabled={confirming || included === 0}
            className="rounded-full bg-white text-black hover:bg-white/90 h-10 px-6" data-testid="intake-confirm-btn">
            {confirming ? "Saving…" : `Save ${included} record${included === 1 ? "" : "s"}`}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-relationship affordance for a routed Intake. Reuses the existing
// canonical Organization + Entity/Project selectors and calls a
// single server endpoint that dedups the (org, entity, project)
// triple. The routed-records section refreshes in place — the user
// never leaves the Intake review.
// ---------------------------------------------------------------------------
function AddIntakeRelationship({ item, entities, projects, onAdded }) {
  const orgLinks = (item.linked_records || []).filter(l => l.type === "organization");
  const [orgId, setOrgId] = React.useState(orgLinks[0]?.id || "");
  const [entityId, setEntityId] = React.useState("");
  const [projectId, setProjectId] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const projList = projects.filter(p => p.entity_id === entityId);
  const activeOrg = orgLinks.find(l => l.id === orgId) || orgLinks[0];
  const orgName = activeOrg?.organization_name || "this organization";

  const add = async () => {
    if (!orgId || !entityId) {
      return toast.error("Pick an entity to link to");
    }
    setSaving(true);
    try {
      const r = await intakeApi.addRelationship(item.id, {
        organization_id: orgId,
        entity_id: entityId,
        project_id: projectId || null,
      });
      if (r.data.duplicate) {
        toast.info("Already linked", {
          description: `${orgName} is already linked to that Entity/Project.`,
        });
      } else {
        toast.success("Relationship added");
      }
      // Reset only the destination; keep the org selector as-is.
      setEntityId(""); setProjectId("");
      onAdded && onAdded(r.data.intake_item);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add relationship");
    } finally { setSaving(false); }
  };

  return (
    <section className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3"
             data-testid="intake-add-relationship">
      <div className="text-eyebrow">Add another relationship</div>
      <div className="text-xs text-white/60">
        {orgName} is already saved as a canonical Organization. Link it to another Entity / Project without creating a duplicate.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {orgLinks.length > 1 && (
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[160px]"
                           data-testid="intake-add-rel-org-select">
              <SelectValue placeholder="Organization"/>
            </SelectTrigger>
            <SelectContent className="bg-[#151A23] border-white/10 text-white">
              {orgLinks.map(l => (
                <SelectItem key={l.id} value={l.id}>
                  {l.organization_name || "Organization"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={entityId} onValueChange={(v) => { setEntityId(v); setProjectId(""); }}>
          <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[180px]"
                         data-testid="intake-add-rel-entity-select">
            <SelectValue placeholder="Select entity"/>
          </SelectTrigger>
          <SelectContent className="bg-[#151A23] border-white/10 text-white">
            {entities.map(e => (
              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={projectId || "__none"}
                onValueChange={(v) => setProjectId(v === "__none" ? "" : v)}
                disabled={!entityId}>
          <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[180px]"
                         data-testid="intake-add-rel-project-select">
            <SelectValue placeholder="No project"/>
          </SelectTrigger>
          <SelectContent className="bg-[#151A23] border-white/10 text-white">
            <SelectItem value="__none">No project</SelectItem>
            {projList.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={add} disabled={saving || !entityId}
                className="rounded-full bg-white text-black hover:bg-white/90 h-8 px-4"
                data-testid="intake-add-rel-submit">
          {saving ? "Adding…" : "Add relationship"}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 403 URL Fetch Recovery — surfaced when the origin refused server-side
// access. The original blocked URL is preserved on the intake item as
// provenance; the user picks a recovery path (screenshot or pasted
// content) that flows through the existing image / text pipelines.
// ---------------------------------------------------------------------------
function FetchBlockedRecovery({ blocked, recoveryMode, setRecoveryMode,
                                onCancel, textVal, setTextVal,
                                imageFile, imagePreview, uploading, onUpload,
                                analyzing, onAnalyze }) {
  return (
    <div className="max-w-2xl space-y-5" data-testid="intake-fetch-blocked">
      <div className="p-4 rounded-xl border border-amber-400/30 bg-amber-500/[0.06] flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5"/>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-white/95">This page couldn't be accessed directly.</div>
          <div className="text-xs text-white/60 mt-1 break-all">{blocked.source_url}</div>
          {blocked.reason && (
            <div className="text-[11px] text-white/50 mt-1">{blocked.reason}</div>
          )}
          <div className="text-xs text-white/70 mt-2">
            HQ will keep this URL as the source of record. Choose a recovery path below.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button" size="sm"
          onClick={() => setRecoveryMode("screenshot")}
          className={`rounded-full h-9 px-4 text-xs ${recoveryMode === "screenshot" ? "bg-white text-black hover:bg-white/90" : "bg-white/[0.06] text-white/85 border border-white/15 hover:bg-white/[0.1]"}`}
          data-testid="intake-recovery-screenshot-btn">
          <ImagePlus className="w-3.5 h-3.5 mr-1.5"/> Upload Screenshot
        </Button>
        <Button
          type="button" size="sm"
          onClick={() => setRecoveryMode("paste")}
          className={`rounded-full h-9 px-4 text-xs ${recoveryMode === "paste" ? "bg-white text-black hover:bg-white/90" : "bg-white/[0.06] text-white/85 border border-white/15 hover:bg-white/[0.1]"}`}
          data-testid="intake-recovery-paste-btn">
          <FileText className="w-3.5 h-3.5 mr-1.5"/> Paste Page Content
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}
          className="text-white/60 hover:text-white hover:bg-white/10 h-9"
          data-testid="intake-recovery-cancel-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1"/> Back to capture
        </Button>
      </div>

      {recoveryMode === "screenshot" && (
        <div className="space-y-3">
          <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/15 rounded-xl p-8 cursor-pointer hover:bg-white/[0.02]"
                 data-testid="intake-recovery-image-drop">
            <ImagePlus className="w-6 h-6 text-white/40"/>
            <div className="text-sm text-white/70">
              {imageFile ? imageFile.name : "Click to upload — PNG, JPG, WEBP"}
            </div>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="intake-recovery-image-file"/>
          </label>
          {imagePreview && (
            <img src={imagePreview} alt="preview" className="max-h-64 rounded-lg border border-white/10"/>
          )}
          <Textarea rows={3} value={textVal} onChange={(e) => setTextVal(e.target.value)}
            placeholder="Optional: caption / what this is"
            className="bg-white/5 border-white/10 text-sm"
            data-testid="intake-recovery-image-caption"/>
          {uploading && <div className="text-xs text-white/50">Uploading…</div>}
        </div>
      )}

      {recoveryMode === "paste" && (
        <Textarea rows={10} value={textVal} onChange={(e) => setTextVal(e.target.value)}
          placeholder="Paste the visible page copy here…"
          className="bg-white/5 border-white/10 text-sm"
          data-testid="intake-recovery-paste-input"/>
      )}

      {recoveryMode && (
        <Button onClick={onAnalyze} disabled={analyzing}
          className="rounded-full bg-white text-black hover:bg-white/90 h-11 px-6"
          data-testid="intake-recovery-analyze-btn">
          <Sparkles className="w-4 h-4 mr-2"/> {analyzing ? "Analysing…" : "Analyse recovery content"}
        </Button>
      )}
      <div className="text-[11px] text-white/40">
        The original URL stays attached as provenance. Recovery content flows through the exact same Intake analysis you'd get from a normal capture.
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Contributor "Submitted to HQ" confirmation — replaces the dead-end
// empty review pane when a restricted Member's submission produced no
// visible routing matches. Two states:
//   • cross_entity_ack=true → private cross-entity signal was captured;
//     copy carries the neutral "Submitted and reviewed" message and
//     mentions HQ review without leaking any hidden entity/project/
//     confidence.
//   • otherwise → "Submitted to HQ · Needs Review"; admin will pick it
//     up later. The submission is already persisted server-side.
// Contributor never sees routing controls for destinations they can't
// access. A single "New submission" button resets the capture form.
// ---------------------------------------------------------------------------
function ContributorSubmittedCard({ ack, onNew }) {
  const isReviewed = ack.cross_entity_ack;
  return (
    <div className="max-w-xl space-y-5" data-testid="intake-submitted-ack">
      <div className={`p-6 rounded-xl border ${
        isReviewed
          ? "border-emerald-400/25 bg-emerald-500/[0.06]"
          : "border-white/10 bg-white/[0.03]"
      } flex items-start gap-4`}>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
          isReviewed ? "bg-emerald-500/20 text-emerald-300"
                     : "bg-white/10 text-white/80"
        }`}>
          <Check className="w-4 h-4"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-eyebrow mb-1">
            {isReviewed ? "Submitted and reviewed" : "Submitted to HQ"}
          </div>
          <h1 className="font-display text-xl font-medium tracking-tight">
            {isReviewed
              ? "This information has been reviewed at HQ."
              : "HQ has your submission."}
          </h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            {isReviewed
              ? "HQ captured what it needs. No further action from you."
              : "It's saved under Intake as Needs Review. An admin will route it, archive it, or turn it into a note when they get to it."}
          </p>
        </div>
      </div>
      <Button onClick={onNew}
        className="rounded-full bg-white text-black hover:bg-white/90 h-11 px-6"
        data-testid="intake-ack-new-submission-btn">
        <Sparkles className="w-4 h-4 mr-2"/> New submission
      </Button>
    </div>
  );
}


function ConfidenceBadge({ sug, value }) {
  const final = typeof sug.final_confidence === "number"
    ? sug.final_confidence
    : (value("confidence") || 0);
  const source = typeof sug.source_confidence === "number"
    ? sug.source_confidence : final;
  const boost = typeof sug.context_boost === "number" ? sug.context_boost : 0;
  const ctx = sug.context || {};
  const [open, setOpen] = React.useState(false);
  const hasBreakdown = boost > 0 || ctx.is_known || ctx.not_pursuing_material;
  return (
    <div className="relative">
      <button type="button" onClick={() => hasBreakdown && setOpen(v => !v)}
        className={`text-[10px] px-1.5 py-0.5 rounded ${hasBreakdown ? "text-white/70 hover:text-white bg-white/5 border border-white/10 cursor-pointer" : "text-white/40"}`}
        data-testid={`intake-confidence-${sug.id}`}
        title={hasBreakdown ? "Show relevance breakdown" : ""}>
        {Math.round(final * 100)}% relevance
        {hasBreakdown && <span className="ml-1 text-white/40">ⓘ</span>}
      </button>
      {open && hasBreakdown && (
        <div className="absolute right-0 top-full mt-1 z-10 w-72 p-3 rounded-lg border border-white/15 bg-[#151A23] text-[11px] text-white/80 space-y-1 shadow-lg"
             data-testid={`intake-confidence-breakdown-${sug.id}`}>
          <div className="flex justify-between">
            <span className="text-white/60">Source match</span>
            <span>{Math.round(source * 100)}%</span>
          </div>
          {boost > 0 && ctx.relationship_status && (
            <div className="flex justify-between text-emerald-300">
              <span>Existing relationship: {ctx.relationship_status}</span>
              <span>+{Math.round(boost * 100)}</span>
            </div>
          )}
          {ctx.canonical_organization && (
            <div className="text-white/60">Known Organization: {ctx.canonical_organization.name}</div>
          )}
          {ctx.canonical_contact && (
            <div className="text-white/60">Known Contact: {ctx.canonical_contact.full_name}</div>
          )}
          {ctx.via_organization && (
            <div className="text-white/60">via {ctx.via_organization.name}</div>
          )}
          {ctx.not_pursuing_material && (
            <div className="text-amber-300">Not Pursuing — materially new evidence override</div>
          )}
          <div className="pt-1 border-t border-white/10 flex justify-between text-white/90">
            <span>Final</span><span>{Math.round(final * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ sug, entities, projects, included, onToggle, value, onUpdate }) {
  const entityId = value("entity_id");
  const projList = projects.filter(p => p.entity_id === entityId);
  const rtype = value("record_type");
  return (
    <div className={`p-4 rounded-xl border transition-colors ${included ? "border-white/20 bg-white/[0.04]" : "border-white/10 bg-white/[0.01] opacity-70"}`} data-testid={`intake-suggestion-${sug.id}`}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={included} onChange={onToggle}
          className="mt-1 w-4 h-4 accent-white" data-testid={`intake-include-${sug.id}`}/>
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={entityId || ""} onValueChange={(v) => onUpdate({ entity_id: v, project_id: null })}>
              <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[160px]" data-testid={`intake-entity-select-${sug.id}`}>
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                {entities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={value("project_id") || "__none"} onValueChange={(v) => onUpdate({ project_id: v === "__none" ? null : v })}>
              <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[160px]">
                <SelectValue placeholder="No project"/>
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__none">No project</SelectItem>
                {projList.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={rtype} onValueChange={(v) => onUpdate({ record_type: v })}>
              <SelectTrigger className="h-8 bg-white/5 border-white/10 text-xs w-auto min-w-[110px]" data-testid={`intake-rtype-${sug.id}`}>
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                {["note", "task", "contact", "organization", "none"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <ConfidenceBadge sug={sug} value={value} />
          </div>

          <Input value={value("title") || ""} onChange={(e) => onUpdate({ title: e.target.value })}
            className="bg-white/5 border-white/10 h-9 text-sm" placeholder="Title" data-testid={`intake-title-${sug.id}`}/>
          <Textarea rows={3} value={value("content") || ""} onChange={(e) => onUpdate({ content: e.target.value })}
            className="bg-white/5 border-white/10 text-sm" placeholder="Content" data-testid={`intake-content-${sug.id}`}/>

          {rtype === "contact" && (
            <ContactSubForm value={value} onUpdate={onUpdate} sid={sug.id}/>
          )}
          {rtype === "organization" && (
            <OrganizationSubForm value={value} onUpdate={onUpdate} sid={sug.id}/>
          )}

              {sug.matched_signals && sug.matched_signals.length > 0 && (
                <div className="p-2.5 rounded-lg bg-emerald-500/[0.05] border border-emerald-400/15">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1.5">Matched signals</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sug.matched_signals.map((s, i) => (
                      <span key={i} className="text-[10px] text-white/85 bg-white/[0.05] border border-white/10 rounded-full px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/5">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Why this destination</div>
                <div className="text-xs text-white/70 leading-relaxed">{sug.why || "—"}</div>
              </div>
        </div>
      </div>
    </div>
  );
}

function ContactSubForm({ value, onUpdate, sid }) {
  const c = value("contact") || {};
  const set = (k, v) => onUpdate({ contact: { ...(c || {}), [k]: v } });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`intake-contact-fields-${sid}`}>
      <Input value={c.full_name || ""} onChange={(e) => set("full_name", e.target.value)}
        placeholder="Full name" className="bg-white/5 border-white/10 h-8 text-xs sm:col-span-2"/>
      <Input value={c.email || ""} onChange={(e) => set("email", e.target.value)}
        placeholder="Email (optional)" className="bg-white/5 border-white/10 h-8 text-xs"/>
      <Input value={c.phone || ""} onChange={(e) => set("phone", e.target.value)}
        placeholder="Phone (optional)" className="bg-white/5 border-white/10 h-8 text-xs"/>
      <Input value={c.company || ""} onChange={(e) => set("company", e.target.value)}
        placeholder="Company (optional)" className="bg-white/5 border-white/10 h-8 text-xs"/>
      <Input value={c.role || ""} onChange={(e) => set("role", e.target.value)}
        placeholder="Role (optional)" className="bg-white/5 border-white/10 h-8 text-xs"/>
    </div>
  );
}

// Organization sub-form for Intake review pane — editable pre-confirmation
// fields for a suggested canonical Organization. Dedup happens server-side
// on confirm; the user cannot accidentally create a duplicate here.
const ORG_TYPES_UI = ["NGO","Foundation","Government","Chamber","Company","Community","Education","Developer","Professional","Other"];
const ORG_STATUSES_UI = ["Researching","Potential Fit","Outreach","Active","Partner","Not Pursuing"];

function OrganizationSubForm({ value, onUpdate, sid }) {
  const o = value("organization") || {};
  const set = (k, v) => onUpdate({ organization: { ...(o || {}), [k]: v } });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-lg border border-emerald-400/15 bg-emerald-500/[0.03]"
         data-testid={`intake-organization-fields-${sid}`}>
      <Input value={o.name || ""} onChange={(e) => set("name", e.target.value)}
        placeholder="Organization name" className="bg-white/5 border-white/10 h-8 text-xs sm:col-span-2"
        data-testid={`intake-org-name-${sid}`}/>
      <Input value={o.website || ""} onChange={(e) => set("website", e.target.value)}
        placeholder="Website (optional)" className="bg-white/5 border-white/10 h-8 text-xs"
        data-testid={`intake-org-website-${sid}`}/>
      <select value={o.organization_type || "Other"}
              onChange={(e) => set("organization_type", e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md h-8 text-xs px-2 text-white"
              data-testid={`intake-org-type-${sid}`}>
        {ORG_TYPES_UI.map(t => <option key={t} value={t} className="bg-[#151A23]">{t}</option>)}
      </select>
      <select value={o.relationship_status || "Potential Fit"}
              onChange={(e) => set("relationship_status", e.target.value)}
              className="bg-white/5 border border-white/10 rounded-md h-8 text-xs px-2 text-white sm:col-span-2"
              data-testid={`intake-org-status-${sid}`}>
        {ORG_STATUSES_UI.map(s => <option key={s} value={s} className="bg-[#151A23]">{s}</option>)}
      </select>
    </div>
  );
}
